import * as i2 from '@angular/common';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, PLATFORM_ID, ElementRef, Component, ViewEncapsulation, Inject, Output, ViewChild, ContentChild, ContentChildren, Input, SkipSelf, NgModule } from '@angular/core';
import { WINDOW } from '@upr-web-primer/window';
import { get } from 'lodash-es';
import { Subject, BehaviorSubject, Subscription, fromEvent, Observable } from 'rxjs';
import { map, distinctUntilChanged, auditTime, debounceTime, mergeMap } from 'rxjs/operators';

class CarouselService {
    constructor() {
        this.$eventDown = new Subject();
        this.$eventUp = new Subject();
        this.$mouseEnter = new Subject();
        this.$mouseLeave = new Subject();
        this.$transitioning = new Subject();
        this.$isVisible = new Subject();
        this.$hasResized = new Subject();
        this.$hasReset = new Subject();
        this.$willReset = new Subject();
        this.$willResize = new Subject();
        this.$willSlide = new Subject();
        this.$loaded = new BehaviorSubject(false);
        this.$triggerReset = new Subject();
        this.$triggerResize = new Subject();
        this.$triggerSlide = new Subject();
        this.$triggerFixHeight = new Subject();
        this.$analytics = new BehaviorSubject('');
        this.$stretch = new BehaviorSubject(false);
        this.$focus = new BehaviorSubject(false);
        this.$playing = new BehaviorSubject(false);
        this.tempData = {};
    }
}
CarouselService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CarouselService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselService, decorators: [{
            type: Injectable
        }] });

class CarouselBreakpoint {
    constructor() {
        this.slidesToShow = 1;
        this.breakpoint = 0;
        this.sliderWidth = 0;
    }
}
class CarouselLabels {
    constructor(options) {
        this.next = options && options.next ? options.next : 'Next';
        this.prev = options && options.prev ? options.prev : 'Previous';
        this.goto = options && options.goto ? options.goto : 'Go to slide';
        this.current =
            options && options.current ? options.current : 'Current Slide';
    }
}
class CarouselOptions {
    constructor(options) {
        this.arrowsAlignment =
            options && options.arrowsAlignment !== undefined
                ? options.arrowsAlignment
                : false;
        this.stretchItems =
            options && options.stretchItems !== undefined
                ? options.stretchItems
                : true;
        this.pagination =
            options && options.pagination !== undefined ? options.pagination : true;
        this.autoplay =
            options && options.autoplay !== undefined ? options.autoplay : true;
        this.arrows =
            options && options.arrows !== undefined ? options.arrows : true;
        this.loop = options && options.loop !== undefined ? options.loop : true;
        this.pauseOnHover =
            options && options.pauseOnHover !== undefined
                ? options.pauseOnHover
                : true;
        this.thumbnails =
            options && options.thumbnails !== undefined ? options.thumbnails : false;
        this.transitionSpeed =
            options && options.transitionSpeed ? options.transitionSpeed : 400;
        this.initSlideIndex =
            options && options.initSlideIndex ? options.initSlideIndex : 1;
        this.edgePercentage =
            options && options.edgePercentage ? options.edgePercentage : 0;
        this.gutterSpace = options && options.gutterSpace ? options.gutterSpace : 0;
        this.delayTime = options && options.delayTime ? options.delayTime : 5000;
        this.height = options && options.height ? options.height : 'auto';
        this.breakpoints =
            options && options.breakpoints ? options.breakpoints : [];
        this.labels =
            options && options.labels ? options.labels : new CarouselLabels();
        this.showArrowsOnHover = !!options && !!options.showArrowsOnHover;
    }
}

class CarouselComponent {
    constructor(render, carouselService, zone, changeDetector, platformId, window, document) {
        this.render = render;
        this.carouselService = carouselService;
        this.zone = zone;
        this.changeDetector = changeDetector;
        this.platformId = platformId;
        this.window = window;
        this.document = document;
        this._transition = false;
        this._currentIndex = 0;
        this.service = new EventEmitter();
        this.carouselOptions = new CarouselOptions();
        this.analytics = 'Body | Carousel';
        this.$subscriptions = new Subscription();
        this.items = [];
        this.clonedItems = [];
        this.extraSlidesEachSide = 0;
        this.canTransition = true;
        this.stretchItems = true;
        this.thumbTrigger = false;
        this.thumbnail = false;
        this.oneSlide = true;
        this.playing = false;
        this.isMouseOver = false;
        this.zone.runOutsideAngular(() => {
            this.$subscriptions.add(fromEvent(this.window, 'resize')
                .pipe(map(() => this.determineDocumentClientWidth()), distinctUntilChanged(), auditTime(100))
                .subscribe(() => this.resizeSlider()));
        });
    }
    set transition(bool) {
        this._transition = bool;
        if (!bool) {
            this.render.addClass(this.slideWrapper.nativeElement, 'no-transition');
            this.render.removeClass(this.slideWrapper.nativeElement, 'transition');
        }
        else {
            this.render.addClass(this.slideWrapper.nativeElement, 'transition');
            this.render.removeClass(this.slideWrapper.nativeElement, 'no-transition');
        }
        if (!bool && !this.isMouseDown) {
            setTimeout(() => {
                if (!this.isMouseDown) {
                    this._transition = true;
                    this.render.addClass(this.slideWrapper.nativeElement, 'transition');
                    this.render.removeClass(this.slideWrapper.nativeElement, 'no-transition');
                    this.canTransition = true;
                }
            }, this.carouselOptions.transitionSpeed + 25);
        }
    }
    get transition() {
        return this._transition;
    }
    set breakpoint(point) {
        if (this._breakpoint && this._breakpoint.breakpoint !== point.breakpoint) {
            this._breakpoint = point;
            this.sliderPrepResize();
            return;
        }
        this._breakpoint = point;
    }
    get breakpoint() {
        return this._breakpoint;
    }
    set currentIndex(index) {
        this._currentIndex = index;
        this.realCurrentIndex = this.getRealCurrentIndex(index);
        this.changeDetector.detectChanges();
    }
    get currentIndex() {
        return this._currentIndex;
    }
    ngOnChanges() {
        if (this.loadedOnce && !this.thumbnail) {
            this.carouselService.$triggerReset.next(this.getEventData());
        }
    }
    ngOnInit() {
        this.service.emit(this.carouselService);
        this.carouselService.$analytics.next(this.analytics);
        this.carouselService.$stretch.next(this.stretchItems);
        this.setupIsVisible();
        this.$subscriptions.add(this.carouselService.$triggerReset
            .pipe(debounceTime(50))
            .subscribe(() => this.sliderPrepOnce()));
        this.$subscriptions.add(this.carouselService.$triggerResize
            .pipe(debounceTime(50))
            .subscribe(() => this.resizeSlider()));
        this.$subscriptions.add(this.carouselService.$triggerFixHeight
            .pipe(debounceTime(200))
            .subscribe(height => {
            this.setFixHeight(height);
            this.changeDetector.detectChanges();
        }));
        if (!this.thumbnail) {
            this.$subscriptions.add(this.carouselService.$playing.subscribe(bool => {
                this.playing = bool;
                if (!this.buttonWrapper) {
                    return;
                }
                if (this.playing) {
                    this.render.addClass(this.buttonWrapper.nativeElement, 'playing');
                }
                else {
                    this.render.removeClass(this.buttonWrapper.nativeElement, 'playing');
                    if (!this.isMouseOver) {
                        this.autoPlayToggle();
                    }
                }
            }));
            this.$subscriptions.add(this.carouselService.$triggerSlide.subscribe(slide => {
                this.slideTo(slide + this.extraSlidesEachSide, undefined, true);
            }));
        }
        else {
            this.$subscriptions.add(this.carouselService.$willSlide.subscribe(slide => {
                this.slideTo(slide + this.extraSlidesEachSide, undefined, true);
            }));
        }
        if (!this.thumbnail && this.toggleFocus) {
            this.$subscriptions.add(this.carouselService.$focus.subscribe((bool) => {
                this.isOnFocus = bool;
                this.autoPlayToggle(!bool);
            }));
        }
    }
    ngAfterContentInit() {
        this.$subscriptions.add(this.slides.changes.subscribe(() => {
            this.carouselService.$triggerReset.next(this.getEventData());
        }));
    }
    getEventData() {
        return {
            currentIndex: this.currentIndex,
            prevIndex: this.prevIndex,
            breakpoint: this.breakpoint,
            slides: this.getSlideElements()
        };
    }
    getSlideElements() {
        const slideWrapper = this.slideWrapper.nativeElement;
        return Array.from(slideWrapper.children);
    }
    setupIsVisible() {
        if (!this.thumbnail) {
            if (isPlatformBrowser(this.platformId) &&
                'IntersectionObserver' in this.window) {
                this.$subscriptions.add(new Observable((observer) => {
                    const intersectionObserver = new IntersectionObserver(entries => {
                        observer.next(entries);
                    });
                    intersectionObserver.observe(this.carouselContainer.nativeElement);
                    return () => {
                        intersectionObserver.disconnect();
                    };
                })
                    .pipe(mergeMap((entries) => entries), map((entry) => entry.isIntersecting || entry.intersectionRatio > 0), distinctUntilChanged())
                    .subscribe((bool) => {
                    this.sliderVisible(bool);
                }));
            }
            else {
                setTimeout(() => {
                    this.sliderVisible(true);
                }, 100);
            }
        }
        else {
            this.$subscriptions.add(this.carouselService.$loaded.subscribe(bool => this.sliderVisible(bool)));
        }
    }
    setupListeners(move) {
        if (move && (!this.mouseMove || this.mouseMove.closed)) {
            this.zone.runOutsideAngular(() => {
                this.mouseMove = fromEvent(this.carouselContainer.nativeElement, 'mousemove').subscribe((event) => this.eventMove(event));
                this.touchMove = fromEvent(this.carouselContainer.nativeElement, 'touchmove').subscribe((event) => this.eventMove(event.changedTouches[0]));
            });
            return;
        }
        if (!this.oneSlide &&
            this.items.length + this.clonedItems.length >
                this.breakpoint.slidesToShow &&
            (!this.mouseDown || this.mouseDown.closed)) {
            this.zone.runOutsideAngular(() => {
                this.mouseDown = fromEvent(this.carouselContainer.nativeElement, 'mousedown').subscribe((event) => this.eventDown(event));
                this.mouseUp = fromEvent(this.carouselContainer.nativeElement, 'mouseup').subscribe((event) => this.eventUp(event));
                this.touchStart = fromEvent(this.carouselContainer.nativeElement, 'touchstart').subscribe((event) => this.eventDown(event.changedTouches[0]));
                this.touchEnd = fromEvent(this.carouselContainer.nativeElement, 'touchend').subscribe((event) => this.eventUp(event.changedTouches[0]));
            });
        }
        if (!this.mouseEnter) {
            this.autoPlayToggle();
            this.zone.runOutsideAngular(() => {
                this.mouseEnter = fromEvent(this.carouselContainer.nativeElement, 'mouseenter').subscribe((event) => this.eventLeaveEnter(event));
                this.mouseLeave = fromEvent(this.carouselContainer.nativeElement, 'mouseleave').subscribe((event) => this.eventLeaveEnter(event));
            });
        }
        if (!this.thumbnail && this.toggleFocus) {
            this.zone.runOutsideAngular(() => {
                this.focusOut = fromEvent(this.carouselContainer.nativeElement, 'focusout').subscribe((event) => this.eventFocusOut(event));
            });
        }
        this.zone.runOutsideAngular(() => {
            this.transitionEnd = fromEvent(this.carouselContainer.nativeElement, 'transitionend').subscribe(() => this.transitionEvent());
        });
    }
    removeListeners(move = false) {
        if (move && this.mouseMove) {
            this.mouseMove.unsubscribe();
            this.touchMove.unsubscribe();
            return;
        }
        if (this.mouseDown) {
            this.mouseDown.unsubscribe();
            this.mouseUp.unsubscribe();
            this.touchStart.unsubscribe();
            this.touchEnd.unsubscribe();
            this.mouseLeave.unsubscribe();
            this.mouseEnter.unsubscribe();
            if (this.mouseMove) {
                this.mouseMove.unsubscribe();
                this.touchMove.unsubscribe();
            }
        }
        if (this.transitionEnd) {
            this.transitionEnd.unsubscribe();
        }
        if (this.focusOut) {
            this.focusOut.unsubscribe();
        }
    }
    eventMove(event) {
        const remove = event.clientX - this.mouseDownPos;
        if (Math.abs(remove) > 25) {
            this.render.addClass(this.slideWrapper.nativeElement, 'dragging');
        }
        this.updateTransform(remove);
    }
    eventDown(event) {
        if (!this.playing) {
            this.autoPlayToggle(false);
            this.setupListeners(true);
            this.isMouseDown = true;
            this.transition = false;
            this.mouseDownPos = event.clientX;
            this.carouselService.$eventDown.next(this.getEventData());
        }
    }
    eventUp(event) {
        if (!this.playing) {
            this.transition = true;
            this.removeListeners(true);
            this.autoPlayToggle();
            this.mouseUpPos = event.clientX;
            this.isMouseDown = false;
            this.render.removeClass(this.slideWrapper.nativeElement, 'dragging');
            const overHalfwidth = this.sliderWidth / 2;
            const dragAmount = Math.abs(this.mouseDownPos - this.mouseUpPos);
            if (dragAmount > overHalfwidth) {
                if (!this.canTransition) {
                    this.canTransition = true;
                }
                this.mouseDownPos > this.mouseUpPos
                    ? this.slide('next')
                    : this.slide('prev');
            }
            else {
                this.slideTo(this.currentIndex);
            }
            this.carouselService.$eventUp.next(this.getEventData());
        }
    }
    eventFocusOut(event) {
        if (this.isOnFocus && event.type === 'focusout') {
            this.isOnFocus = false;
            this.autoPlayToggle(true);
        }
    }
    eventLeaveEnter(event) {
        if (event.type === 'mouseleave') {
            this.carouselService.$mouseLeave.next(this.getEventData());
            this.autoPlayToggle();
            if (this.isMouseDown) {
                this.eventUp(event);
            }
            this.isMouseOver = false;
            return;
        }
        this.isMouseOver = true;
        this.carouselService.$mouseEnter.next(this.getEventData());
        this.autoPlayToggle(false);
    }
    transitionEvent() {
        this.canTransition = true;
        if (!this.thumbnail) {
            this.carouselService.$transitioning.next(this.getEventData());
        }
    }
    resizeSlider() {
        if (this.loadedOnce && !this.thumbnail) {
            this.carouselService.$willResize.next(this.getEventData());
        }
        else if (!this.loadedOnce) {
            return;
        }
        this.transition = false;
        this.breakpoint = this.currentBreakpoint();
        this.sliderPrep();
        this.setFixHeight();
        if (!this.thumbnail) {
            this.carouselService.$hasResized.next(this.getEventData());
        }
    }
    sliderVisible(visible) {
        this.isVisible = visible;
        if (!this.thumbnail) {
            this.carouselService.$isVisible.next(this.getEventData());
        }
        if (!this.loadedOnce && visible) {
            this.loadedOnce = true;
            this.carouselService.$triggerReset.next(this.getEventData());
            if (!this.thumbnail) {
                this.carouselService.$loaded.next(true);
            }
        }
        if (visible && this.sliderWidth === 0) {
            this.sliderPrep();
        }
    }
    sliderPrepOnce() {
        if (this.loadedOnce && !this.thumbnail) {
            this.carouselService.$willReset.next(this.getEventData());
        }
        else if (!this.loadedOnce) {
            return;
        }
        this.initItems = this.slides
            .toArray()
            .map(elementRef => elementRef.nativeElement);
        if (this.carouselOptions.breakpoints &&
            this.carouselOptions.breakpoints.length > 0) {
            this.carouselOptions.breakpoints.sort((last, current) => last.breakpoint > current.breakpoint ? 0 : 1);
        }
        this.resizeSlider();
        this.currentIndex = get(this.carouselOptions, 'initSlideIndex', 1);
        this.sliderHeightString = `${this.carouselOptions.height}px`;
        this.transitionSpeed = `${this.carouselOptions.transitionSpeed / 1000}s`;
        this.sliderPrepResize();
        this.autoPlayToggle();
        this.updateTransform();
    }
    sliderPrepResize() {
        this.removeListeners();
        const items = this.initItems || [];
        if (this.clonedItems.length) {
            this.clonedItems.forEach(clone => {
                this.render.removeChild(this.slideWrapper.nativeElement, clone);
            });
            this.clonedItems = [];
        }
        if ((items.length > this.breakpoint.slidesToShow &&
            this.carouselOptions.loop) ||
            (this.carouselOptions.loop && this.thumbnail)) {
            this.extraSlidesEachSide =
                this.breakpoint.slidesToShow +
                    (this.carouselOptions.edgePercentage > 0 ? 1 : 0);
            const newItems = [];
            const before = items.slice(-this.extraSlidesEachSide);
            const after = items.slice(0, this.extraSlidesEachSide);
            this.items = newItems.concat(before, items, after);
            before.reverse().forEach(item => {
                const clone = item.cloneNode(true);
                this.setupClonedItem(clone);
                this.clonedItems.push(clone);
                this.render.insertBefore(this.slideWrapper.nativeElement, clone, this.slideWrapper.nativeElement.firstChild);
            });
            after.forEach(item => {
                const clone = item.cloneNode(true);
                this.setupClonedItem(clone);
                this.clonedItems.push(clone);
                this.render.appendChild(this.slideWrapper.nativeElement, clone);
            });
            this.initLastIndex = this.items.length - this.extraSlidesEachSide - 1;
            this.initFirstIndex = this.extraSlidesEachSide;
            const slideToShowOnInit = this.thumbnail
                ? this.extraSlidesEachSide + 2
                : this.carouselOptions.initSlideIndex === 1 &&
                    this.breakpoint.slidesToShow === 1
                    ? this.initFirstIndex
                    : this.carouselOptions.initSlideIndex + this.initFirstIndex - 1;
            this.currentIndex = this.loadedOnce
                ? slideToShowOnInit
                : this.currentIndex;
            this.numberPagination = Array(items.length).fill(0);
            this.stretchItems = true;
        }
        else {
            this.items = items;
            this.currentIndex = 0;
            this.initFirstIndex = 0;
            this.initLastIndex = this.items.length - 1;
            this.render.setStyle(this.slideWrapper.nativeElement, 'transform', 'translate3d(-0px, 0px, 0px)');
            this.extraSlidesEachSide = 0;
            this.numberPagination = Array(this.items.length).fill(0);
            if (this.carouselOptions.height !== 'auto') {
                this.stretchItems = this.carouselOptions.stretchItems;
            }
        }
        this.setActive(this.currentIndex, this.currentIndex);
        this.oneSlide = this.items.length === this.breakpoint.slidesToShow;
        this.gutterSpace =
            this.carouselOptions.edgePercentage > 0 ||
                this.breakpoint.slidesToShow > 1
                ? `0 ${this.carouselOptions.gutterSpace}px`
                : '0';
        this.sliderPrep();
        this.setFixHeight();
        this.setupListeners();
        this.carouselService.$stretch.next(this.stretchItems);
        if (!this.thumbnail) {
            this.carouselService.$hasReset.next(this.getEventData());
        }
    }
    setupClonedItem(clone) {
        this.render.addClass(clone, 'clone');
        clone.onclick = ($event) => this.slideTo(Number($event.target.dataset.slideId));
    }
    sliderPrep() {
        const carouselContainerElement = this.carouselContainer
            .nativeElement;
        const slideWrapperElement = this.slideWrapper.nativeElement;
        const documentClientWidth = this.determineDocumentClientWidth();
        const carouselWidth = carouselContainerElement.clientWidth > documentClientWidth
            ? documentClientWidth
            : carouselContainerElement.clientWidth;
        this.sliderWidth =
            this.breakpoint.sliderWidth && this.breakpoint.sliderWidth > 0
                ? this.breakpoint.sliderWidth
                : carouselWidth / this.breakpoint.slidesToShow;
        const isLoop = this.carouselOptions.loop
            ? 0 - this.extraSlidesEachSide + this.clonedItems.length
            : this.breakpoint.slidesToShow;
        if (this.breakpoint.slidesToShow > 1 &&
            this.carouselOptions.edgePercentage === 0) {
            this.sliderWidth =
                (carouselWidth -
                    this.carouselOptions.gutterSpace *
                        (this.breakpoint.slidesToShow - 1)) /
                    this.breakpoint.slidesToShow;
        }
        else if (this.breakpoint.slidesToShow === 1 &&
            this.carouselOptions.edgePercentage !== 0) {
            this.sliderWidth =
                carouselWidth -
                    this.carouselOptions.gutterSpace * 2 -
                    this.sliderWidth * (this.carouselOptions.edgePercentage / 100) * 2;
        }
        this.maxSlide =
            (this.items.length - isLoop) * this.sliderWidth +
                (this.oneSlide ? 0 : this.carouselOptions.gutterSpace / 2);
        this.sliderWidthString = `${this.sliderWidth}px`;
        Array.from(slideWrapperElement.children).forEach((child, index) => {
            this.render.setStyle(child, 'width', this.sliderWidthString);
            this.render.setAttribute(child, 'data-slide-id', index);
            const margin = index !== 0 &&
                index !== slideWrapperElement.children.length &&
                index % 2 !== 0
                ? this.gutterSpace
                : '';
            if (margin) {
                this.render.setStyle(child, 'margin', margin);
            }
            else {
                this.render.removeStyle(child, 'margin');
            }
        });
        this.updateTransform();
    }
    setFixHeight(height) {
        let newHeight = 0;
        if (!this.buttonWrapper) {
            return;
        }
        if (this.fixToHeight && this.buttonWrapper) {
            const element = this.fixToHeight.nativeElement;
            newHeight = element.clientHeight;
            if (newHeight === 0) {
                this.render.setStyle(element, 'display', 'block');
                newHeight = element.clientHeight;
                this.render.setStyle(element, 'display', 'inline');
            }
        }
        else if (height && this.fixToHeight) {
            newHeight = height;
        }
        this.render.setStyle(this.buttonWrapper.nativeElement, 'height', newHeight === 0 ? '100%' : `${newHeight}px`);
    }
    currentBreakpoint() {
        let breakpoint = new CarouselBreakpoint();
        if (this.carouselOptions.breakpoints &&
            this.carouselOptions.breakpoints.length > 0) {
            const currentWidth = this.window.innerWidth;
            const current = this.carouselOptions.breakpoints.filter(point => currentWidth >= point.breakpoint)[0];
            breakpoint = current ? current : breakpoint;
        }
        return breakpoint;
    }
    getRealCurrentIndex(index) {
        this.prevIndex = this._currentIndex;
        let actual = index - this.extraSlidesEachSide;
        if (this.initFirstIndex && index < this.initFirstIndex) {
            actual = 0;
        }
        else if (this.initLastIndex && index > this.initLastIndex) {
            actual = this.numberPagination.length - 1;
        }
        return actual;
    }
    slide(direction) {
        if (!this.canTransition) {
            return;
        }
        this.canTransition = false;
        if (!this.thumbnail) {
            this.carouselService.$transitioning.next(this.getEventData());
        }
        this.transition = true;
        const move = direction === 'next' ? 1 : -1;
        this.slideTo(this.currentIndex + move);
    }
    slideTo(slideIndex, transition = true, reset = false) {
        const goToSlide = (index, animate = transition) => {
            setTimeout(() => {
                if (this.currentIndex !== index) {
                    this.playing = false;
                    this.carouselService.$playing.next(false);
                }
                this.transition = animate;
                this.setActive(this.currentIndex, index);
                this.currentIndex = index;
                if (reset) {
                    this.autoPlayToggle();
                }
                this.updateTransform();
            }, animate ? 0 : this.carouselOptions.transitionSpeed + 25);
        };
        if (!this.carouselOptions.loop &&
            (slideIndex > this.initLastIndex || slideIndex < this.initFirstIndex) &&
            !this.thumbnail) {
            this.canTransition = true;
            return;
        }
        let loopReset = 0;
        let slide = slideIndex;
        let extra = this.extraSlidesEachSide;
        let opposite = 0;
        if (slideIndex > this.initLastIndex) {
            loopReset = +1;
            slide = this.initLastIndex;
            opposite = this.initFirstIndex;
            extra = +this.extraSlidesEachSide;
        }
        else if (slideIndex < this.initFirstIndex) {
            loopReset = -1;
            slide = this.initFirstIndex;
            opposite = this.initLastIndex;
            extra = -this.extraSlidesEachSide;
        }
        if (!this.carouselOptions.loop) {
            loopReset = 0;
        }
        if (!this.thumbnail) {
            this.carouselService.$willSlide.next(this.getRealCurrentIndex(slide) + loopReset);
        }
        goToSlide(slide + loopReset);
        if (loopReset !== 0) {
            goToSlide(opposite, false);
            if (this.thumbnail) {
                if (this.thumbTrigger) {
                    this.thumbTrigger = false;
                    return;
                }
                this.thumbTrigger = true;
                this.carouselService.$triggerSlide.next(slideIndex + extra);
            }
        }
    }
    setActive(prev, next) {
        if (this.items.length > 0) {
            this.render.removeClass(this.items[prev], 'active');
            this.render.setAttribute(this.items[prev], 'aria-current', 'false');
            this.render.addClass(this.items[next], 'active');
            this.render.setAttribute(this.items[next], 'aria-current', 'true');
        }
    }
    updateTransform(remove = 0) {
        const step = () => {
            const element = this.carouselContainer.nativeElement;
            const addGutter = this.carouselOptions.edgePercentage > 0 ||
                this.breakpoint.slidesToShow > 1
                ? this.carouselOptions.gutterSpace
                : 0;
            const initialPos = (this.sliderWidth + addGutter) * this.currentIndex;
            const center = this.breakpoint.slidesToShow % 2 === 0 || !this.carouselOptions.loop
                ? 0
                : (element.clientWidth - this.sliderWidth) / 2;
            let slideContentXPos = initialPos - center - remove;
            slideContentXPos =
                slideContentXPos > this.maxSlide
                    ? this.maxSlide
                    : slideContentXPos < 0
                        ? 0
                        : slideContentXPos;
            this.render.setStyle(this.slideWrapper.nativeElement, 'transform', `translate3d(-${slideContentXPos}px, 0px, 0px)`);
        };
        this.zone.runOutsideAngular(() => {
            this.window.requestAnimationFrame(step);
        });
    }
    autoPlayToggle(autoplay = true) {
        clearInterval(this.autoplayTimer);
        if (!this.playing &&
            this.carouselOptions.autoplay &&
            !this.oneSlide &&
            !this.thumbnail) {
            this.autoplayTimer = setInterval(() => {
                if (!this.isVisible) {
                    return;
                }
                if (this.playing) {
                    clearInterval(this.autoplayTimer);
                    return;
                }
                if (this.carouselOptions.pauseOnHover && autoplay) {
                    this.slide('next');
                }
                else if (!this.carouselOptions.pauseOnHover) {
                    this.slide('next');
                }
            }, this.carouselOptions.delayTime);
        }
    }
    changePlaying(bool) {
        this.playing = bool;
    }
    getPaginationAriaLabel(index) {
        let ariaLabel = '';
        if (this.carouselOptions.labels) {
            ariaLabel = this.carouselOptions.labels.goto + ' ' + (index + 1);
            if (index === this.realCurrentIndex) {
                ariaLabel += ' (' + this.carouselOptions.labels.current + ')';
            }
        }
        return ariaLabel;
    }
    ngOnDestroy() {
        clearInterval(this.autoplayTimer);
        this.removeListeners();
        this.$subscriptions.unsubscribe();
    }
    determineDocumentClientWidth() {
        return (this.document.documentElement.clientWidth ||
            this.document.body.clientWidth ||
            this.window.innerWidth);
    }
}
CarouselComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselComponent, deps: [{ token: i0.Renderer2 }, { token: CarouselService }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: PLATFORM_ID }, { token: WINDOW }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
CarouselComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CarouselComponent, selector: "upr-carousel", inputs: { carouselOptions: "carouselOptions", analytics: "analytics", toggleFocus: "toggleFocus" }, outputs: { service: "service" }, providers: [CarouselService], queries: [{ propertyName: "dot", first: true, predicate: ["dot"], descendants: true }, { propertyName: "fixToHeight", first: true, predicate: ["fixToHeight"], descendants: true, read: ElementRef }, { propertyName: "slides", predicate: ["slide"], read: ElementRef }], viewQueries: [{ propertyName: "carouselContainer", first: true, predicate: ["carouselContainer"], descendants: true, static: true }, { propertyName: "buttonWrapper", first: true, predicate: ["buttonWrapper"], descendants: true, read: ElementRef }, { propertyName: "slideWrapper", first: true, predicate: ["slideWrapper"], descendants: true, read: ElementRef, static: true }], usesOnChanges: true, ngImport: i0, template: "<section class=\"carousel\" data-aui=\"carousel\">\r\n\t<div #carouselContainer class=\"carousel-container\">\r\n\t\t<div\r\n\t\t\tclass=\"slide-wrapper\"\r\n\t\t\t#slideWrapper\r\n\t\t\t[ngClass]=\"{ nostretch: !stretchItems }\"\r\n\t\t\t[ngStyle]=\"{\r\n\t\t\t\theight: sliderHeightString,\r\n\t\t\t\ttransition: transitionSpeed\r\n\t\t\t}\"\r\n\t\t>\r\n\t\t\t<ng-content></ng-content>\r\n\t\t</div>\r\n\t</div>\r\n\r\n\t<div\r\n\t\t*ngIf=\"!oneSlide\"\r\n\t\t#buttonWrapper\r\n\t\tclass=\"button-wrapper\"\r\n\t\t[ngClass]=\"{ 'arrows-bottom': carouselOptions?.arrowsAlignment }\"\r\n\t>\r\n\t\t<button\r\n\t\t\t(click)=\"slide('prev')\"\r\n\t\t\tclass=\"arrow prev\"\r\n\t\t\tdata-aui=\"arrow-prev\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Previous\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.prev\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".prev\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-left\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<button\r\n\t\t\t(click)=\"slide('next')\"\r\n\t\t\tclass=\"arrow next\"\r\n\t\t\tdata-aui=\"arrow-next\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Next\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.next\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".next\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-right\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<div class=\"pagination-container\" *ngIf=\"carouselOptions.pagination\">\r\n\t\t\t<div\r\n\t\t\t\tclass=\"dot-wrapper\"\r\n\t\t\t\t*ngFor=\"let _ of numberPagination; let i = index\"\r\n\t\t\t\t[ngClass]=\"i === realCurrentIndex ? 'active' : ''\"\r\n\t\t\t>\r\n\t\t\t\t<button\r\n\t\t\t\t\tclass=\"pagination\"\r\n\t\t\t\t\tattr.data-aui=\"pagination-dot {{ i + 1 }}\"\r\n\t\t\t\t\t(click)=\"slideTo(i + extraSlidesEachSide, undefined, true)\"\r\n\t\t\t\t\t[attr.aria-label]=\"getPaginationAriaLabel(i)\"\r\n\t\t\t\t>\r\n\t\t\t\t\t<ng-container *ngTemplateOutlet=\"dot\"></ng-container>\r\n\t\t\t\t</button>\r\n\t\t\t</div>\r\n\t\t</div>\r\n\t</div>\r\n</section>\r\n<section *ngIf=\"loadedOnce\" class=\"thumbnails-container\">\r\n\t<ng-content select=\".thumbnails\"></ng-content>\r\n</section>\r\n", styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}.carousel{height:auto;width:100%;overflow:hidden;position:relative}.carousel .carousel-container{width:100%;-webkit-overflow-scrolling:touch}.carousel .carousel-container .slide-wrapper{width:100%;display:flex;flex-direction:row;will-change:transform}.carousel .carousel-container .slide-wrapper.dragging .slide{-webkit-user-select:none;user-select:none}.carousel .carousel-container .slide-wrapper.nostretch{justify-content:center;display:inline-block;text-align:center}.carousel .carousel-container .slide-wrapper.nostretch .slide{width:auto!important;display:inline-block;height:100%}.carousel .carousel-container .slide-wrapper .slide{flex:1 0 auto;height:auto;width:100%;-webkit-user-select:text;user-select:text}.carousel .carousel-container .slide-wrapper.no-transition{transition:none!important}.carousel .carousel-container .slide-wrapper.transition{transition:transform .4s}.carousel .button-wrapper{height:100%;width:100%;position:absolute;top:0;left:0;pointer-events:none}@media print,screen and (max-width: 37.49875em){.carousel .button-wrapper.arrows-bottom .arrow{top:initial;bottom:0}}.carousel .button-wrapper .arrow{cursor:pointer;pointer-events:auto;height:60px;width:60px;position:absolute;top:50%;transform:translateY(-50%);background:transparent;border:none}.carousel .button-wrapper .arrow.prev{left:0}.carousel .button-wrapper .arrow.next{right:0}.carousel .button-wrapper .arrow i{font-size:48px;color:#fff}.carousel .button-wrapper .arrow i.icon-arrow-right{right:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-right{right:16px}}.carousel .button-wrapper .arrow i.icon-arrow-left{left:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-left{left:16px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i{font-size:64px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow{width:72px;height:72px}}.carousel .button-wrapper .hide-arrows{display:none}.carousel .button-wrapper .pagination-container{position:absolute;display:flex;left:50%;transform:translate(-50%);bottom:0;padding:20px 0 16px;flex-direction:row;align-content:center;justify-content:center;pointer-events:auto}.carousel .button-wrapper .pagination-container .dot-wrapper{padding:0 16px}.carousel .button-wrapper .pagination-container .dot-wrapper .pagination:empty{width:12px;height:12px;border:1px solid #fff;border-radius:50%;cursor:pointer;background-color:#3339;padding:0}.carousel .button-wrapper .pagination-container .dot-wrapper.active .pagination:empty{background-color:#fff;border:1px solid #333}.carousel:hover .hide-arrows{display:block}\n"], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'upr-carousel', providers: [CarouselService], encapsulation: ViewEncapsulation.None, template: "<section class=\"carousel\" data-aui=\"carousel\">\r\n\t<div #carouselContainer class=\"carousel-container\">\r\n\t\t<div\r\n\t\t\tclass=\"slide-wrapper\"\r\n\t\t\t#slideWrapper\r\n\t\t\t[ngClass]=\"{ nostretch: !stretchItems }\"\r\n\t\t\t[ngStyle]=\"{\r\n\t\t\t\theight: sliderHeightString,\r\n\t\t\t\ttransition: transitionSpeed\r\n\t\t\t}\"\r\n\t\t>\r\n\t\t\t<ng-content></ng-content>\r\n\t\t</div>\r\n\t</div>\r\n\r\n\t<div\r\n\t\t*ngIf=\"!oneSlide\"\r\n\t\t#buttonWrapper\r\n\t\tclass=\"button-wrapper\"\r\n\t\t[ngClass]=\"{ 'arrows-bottom': carouselOptions?.arrowsAlignment }\"\r\n\t>\r\n\t\t<button\r\n\t\t\t(click)=\"slide('prev')\"\r\n\t\t\tclass=\"arrow prev\"\r\n\t\t\tdata-aui=\"arrow-prev\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Previous\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.prev\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".prev\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-left\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<button\r\n\t\t\t(click)=\"slide('next')\"\r\n\t\t\tclass=\"arrow next\"\r\n\t\t\tdata-aui=\"arrow-next\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Next\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.next\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".next\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-right\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<div class=\"pagination-container\" *ngIf=\"carouselOptions.pagination\">\r\n\t\t\t<div\r\n\t\t\t\tclass=\"dot-wrapper\"\r\n\t\t\t\t*ngFor=\"let _ of numberPagination; let i = index\"\r\n\t\t\t\t[ngClass]=\"i === realCurrentIndex ? 'active' : ''\"\r\n\t\t\t>\r\n\t\t\t\t<button\r\n\t\t\t\t\tclass=\"pagination\"\r\n\t\t\t\t\tattr.data-aui=\"pagination-dot {{ i + 1 }}\"\r\n\t\t\t\t\t(click)=\"slideTo(i + extraSlidesEachSide, undefined, true)\"\r\n\t\t\t\t\t[attr.aria-label]=\"getPaginationAriaLabel(i)\"\r\n\t\t\t\t>\r\n\t\t\t\t\t<ng-container *ngTemplateOutlet=\"dot\"></ng-container>\r\n\t\t\t\t</button>\r\n\t\t\t</div>\r\n\t\t</div>\r\n\t</div>\r\n</section>\r\n<section *ngIf=\"loadedOnce\" class=\"thumbnails-container\">\r\n\t<ng-content select=\".thumbnails\"></ng-content>\r\n</section>\r\n", styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}.carousel{height:auto;width:100%;overflow:hidden;position:relative}.carousel .carousel-container{width:100%;-webkit-overflow-scrolling:touch}.carousel .carousel-container .slide-wrapper{width:100%;display:flex;flex-direction:row;will-change:transform}.carousel .carousel-container .slide-wrapper.dragging .slide{-webkit-user-select:none;user-select:none}.carousel .carousel-container .slide-wrapper.nostretch{justify-content:center;display:inline-block;text-align:center}.carousel .carousel-container .slide-wrapper.nostretch .slide{width:auto!important;display:inline-block;height:100%}.carousel .carousel-container .slide-wrapper .slide{flex:1 0 auto;height:auto;width:100%;-webkit-user-select:text;user-select:text}.carousel .carousel-container .slide-wrapper.no-transition{transition:none!important}.carousel .carousel-container .slide-wrapper.transition{transition:transform .4s}.carousel .button-wrapper{height:100%;width:100%;position:absolute;top:0;left:0;pointer-events:none}@media print,screen and (max-width: 37.49875em){.carousel .button-wrapper.arrows-bottom .arrow{top:initial;bottom:0}}.carousel .button-wrapper .arrow{cursor:pointer;pointer-events:auto;height:60px;width:60px;position:absolute;top:50%;transform:translateY(-50%);background:transparent;border:none}.carousel .button-wrapper .arrow.prev{left:0}.carousel .button-wrapper .arrow.next{right:0}.carousel .button-wrapper .arrow i{font-size:48px;color:#fff}.carousel .button-wrapper .arrow i.icon-arrow-right{right:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-right{right:16px}}.carousel .button-wrapper .arrow i.icon-arrow-left{left:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-left{left:16px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i{font-size:64px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow{width:72px;height:72px}}.carousel .button-wrapper .hide-arrows{display:none}.carousel .button-wrapper .pagination-container{position:absolute;display:flex;left:50%;transform:translate(-50%);bottom:0;padding:20px 0 16px;flex-direction:row;align-content:center;justify-content:center;pointer-events:auto}.carousel .button-wrapper .pagination-container .dot-wrapper{padding:0 16px}.carousel .button-wrapper .pagination-container .dot-wrapper .pagination:empty{width:12px;height:12px;border:1px solid #fff;border-radius:50%;cursor:pointer;background-color:#3339;padding:0}.carousel .button-wrapper .pagination-container .dot-wrapper.active .pagination:empty{background-color:#fff;border:1px solid #333}.carousel:hover .hide-arrows{display:block}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: CarouselService }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { service: [{
                type: Output
            }], carouselContainer: [{
                type: ViewChild,
                args: ['carouselContainer', { static: true }]
            }], buttonWrapper: [{
                type: ViewChild,
                args: ['buttonWrapper', { read: ElementRef }]
            }], slideWrapper: [{
                type: ViewChild,
                args: ['slideWrapper', { read: ElementRef, static: true }]
            }], dot: [{
                type: ContentChild,
                args: ['dot']
            }], fixToHeight: [{
                type: ContentChild,
                args: ['fixToHeight', { read: ElementRef }]
            }], slides: [{
                type: ContentChildren,
                args: ['slide', { read: ElementRef }]
            }], carouselOptions: [{
                type: Input
            }], analytics: [{
                type: Input
            }], toggleFocus: [{
                type: Input
            }] } });

class CarouselThumbnailsComponent extends CarouselComponent {
    constructor(render, zone, changeDetector, carouselService, platformId, window, document) {
        super(render, carouselService, zone, changeDetector, platformId, window, document);
        this.render = render;
        this.zone = zone;
        this.changeDetector = changeDetector;
        this.carouselService = carouselService;
        this.platformId = platformId;
        this.window = window;
        this.document = document;
        this.thumbnail = true;
    }
}
CarouselThumbnailsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselThumbnailsComponent, deps: [{ token: i0.Renderer2 }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: CarouselService, skipSelf: true }, { token: PLATFORM_ID }, { token: WINDOW }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
CarouselThumbnailsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CarouselThumbnailsComponent, selector: "upr-carousel-thumbnails", usesInheritance: true, ngImport: i0, template: "<section class=\"carousel\" data-aui=\"carousel\">\r\n\t<div #carouselContainer class=\"carousel-container\">\r\n\t\t<div\r\n\t\t\tclass=\"slide-wrapper\"\r\n\t\t\t#slideWrapper\r\n\t\t\t[ngClass]=\"{ nostretch: !stretchItems }\"\r\n\t\t\t[ngStyle]=\"{\r\n\t\t\t\theight: sliderHeightString,\r\n\t\t\t\ttransition: transitionSpeed\r\n\t\t\t}\"\r\n\t\t>\r\n\t\t\t<ng-content></ng-content>\r\n\t\t</div>\r\n\t</div>\r\n\r\n\t<div\r\n\t\t*ngIf=\"!oneSlide\"\r\n\t\t#buttonWrapper\r\n\t\tclass=\"button-wrapper\"\r\n\t\t[ngClass]=\"{ 'arrows-bottom': carouselOptions?.arrowsAlignment }\"\r\n\t>\r\n\t\t<button\r\n\t\t\t(click)=\"slide('prev')\"\r\n\t\t\tclass=\"arrow prev\"\r\n\t\t\tdata-aui=\"arrow-prev\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Previous\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.prev\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".prev\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-left\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<button\r\n\t\t\t(click)=\"slide('next')\"\r\n\t\t\tclass=\"arrow next\"\r\n\t\t\tdata-aui=\"arrow-next\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Next\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.next\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".next\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-right\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<div class=\"pagination-container\" *ngIf=\"carouselOptions.pagination\">\r\n\t\t\t<div\r\n\t\t\t\tclass=\"dot-wrapper\"\r\n\t\t\t\t*ngFor=\"let _ of numberPagination; let i = index\"\r\n\t\t\t\t[ngClass]=\"i === realCurrentIndex ? 'active' : ''\"\r\n\t\t\t>\r\n\t\t\t\t<button\r\n\t\t\t\t\tclass=\"pagination\"\r\n\t\t\t\t\tattr.data-aui=\"pagination-dot {{ i + 1 }}\"\r\n\t\t\t\t\t(click)=\"slideTo(i + extraSlidesEachSide, undefined, true)\"\r\n\t\t\t\t\t[attr.aria-label]=\"getPaginationAriaLabel(i)\"\r\n\t\t\t\t>\r\n\t\t\t\t\t<ng-container *ngTemplateOutlet=\"dot\"></ng-container>\r\n\t\t\t\t</button>\r\n\t\t\t</div>\r\n\t\t</div>\r\n\t</div>\r\n</section>\r\n<section *ngIf=\"loadedOnce\" class=\"thumbnails-container\">\r\n\t<ng-content select=\".thumbnails\"></ng-content>\r\n</section>\r\n", styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}.carousel{height:auto;width:100%;overflow:hidden;position:relative}.carousel .carousel-container{width:100%;-webkit-overflow-scrolling:touch}.carousel .carousel-container .slide-wrapper{width:100%;display:flex;flex-direction:row;will-change:transform}.carousel .carousel-container .slide-wrapper.dragging .slide{-webkit-user-select:none;user-select:none}.carousel .carousel-container .slide-wrapper.nostretch{justify-content:center;display:inline-block;text-align:center}.carousel .carousel-container .slide-wrapper.nostretch .slide{width:auto!important;display:inline-block;height:100%}.carousel .carousel-container .slide-wrapper .slide{flex:1 0 auto;height:auto;width:100%;-webkit-user-select:text;user-select:text}.carousel .carousel-container .slide-wrapper.no-transition{transition:none!important}.carousel .carousel-container .slide-wrapper.transition{transition:transform .4s}.carousel .button-wrapper{height:100%;width:100%;position:absolute;top:0;left:0;pointer-events:none}@media print,screen and (max-width: 37.49875em){.carousel .button-wrapper.arrows-bottom .arrow{top:initial;bottom:0}}.carousel .button-wrapper .arrow{cursor:pointer;pointer-events:auto;height:60px;width:60px;position:absolute;top:50%;transform:translateY(-50%);background:transparent;border:none}.carousel .button-wrapper .arrow.prev{left:0}.carousel .button-wrapper .arrow.next{right:0}.carousel .button-wrapper .arrow i{font-size:48px;color:#fff}.carousel .button-wrapper .arrow i.icon-arrow-right{right:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-right{right:16px}}.carousel .button-wrapper .arrow i.icon-arrow-left{left:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-left{left:16px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i{font-size:64px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow{width:72px;height:72px}}.carousel .button-wrapper .hide-arrows{display:none}.carousel .button-wrapper .pagination-container{position:absolute;display:flex;left:50%;transform:translate(-50%);bottom:0;padding:20px 0 16px;flex-direction:row;align-content:center;justify-content:center;pointer-events:auto}.carousel .button-wrapper .pagination-container .dot-wrapper{padding:0 16px}.carousel .button-wrapper .pagination-container .dot-wrapper .pagination:empty{width:12px;height:12px;border:1px solid #fff;border-radius:50%;cursor:pointer;background-color:#3339;padding:0}.carousel .button-wrapper .pagination-container .dot-wrapper.active .pagination:empty{background-color:#fff;border:1px solid #333}.carousel:hover .hide-arrows{display:block}\n", ".thumbnails .carousel .carousel-container .slide-wrapper .clone *{pointer-events:none}\n"], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselThumbnailsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'upr-carousel-thumbnails', encapsulation: ViewEncapsulation.None, template: "<section class=\"carousel\" data-aui=\"carousel\">\r\n\t<div #carouselContainer class=\"carousel-container\">\r\n\t\t<div\r\n\t\t\tclass=\"slide-wrapper\"\r\n\t\t\t#slideWrapper\r\n\t\t\t[ngClass]=\"{ nostretch: !stretchItems }\"\r\n\t\t\t[ngStyle]=\"{\r\n\t\t\t\theight: sliderHeightString,\r\n\t\t\t\ttransition: transitionSpeed\r\n\t\t\t}\"\r\n\t\t>\r\n\t\t\t<ng-content></ng-content>\r\n\t\t</div>\r\n\t</div>\r\n\r\n\t<div\r\n\t\t*ngIf=\"!oneSlide\"\r\n\t\t#buttonWrapper\r\n\t\tclass=\"button-wrapper\"\r\n\t\t[ngClass]=\"{ 'arrows-bottom': carouselOptions?.arrowsAlignment }\"\r\n\t>\r\n\t\t<button\r\n\t\t\t(click)=\"slide('prev')\"\r\n\t\t\tclass=\"arrow prev\"\r\n\t\t\tdata-aui=\"arrow-prev\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Previous\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.prev\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".prev\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-left\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<button\r\n\t\t\t(click)=\"slide('next')\"\r\n\t\t\tclass=\"arrow next\"\r\n\t\t\tdata-aui=\"arrow-next\"\r\n\t\t\t*ngIf=\"carouselOptions.arrows\"\r\n\t\t\tattr.analytics-title=\"{{ analytics }} | Arrows | Next\"\r\n\t\t\t[attr.aria-label]=\"carouselOptions?.labels?.next\"\r\n\t\t\t[ngClass]=\"{ 'hide-arrows': carouselOptions?.showArrowsOnHover }\"\r\n\t\t>\r\n\t\t\t<ng-content select=\".next\"></ng-content>\r\n\t\t\t<i class=\"icon-arrow-right\" aria-hidden=\"true\"></i>\r\n\t\t</button>\r\n\r\n\t\t<div class=\"pagination-container\" *ngIf=\"carouselOptions.pagination\">\r\n\t\t\t<div\r\n\t\t\t\tclass=\"dot-wrapper\"\r\n\t\t\t\t*ngFor=\"let _ of numberPagination; let i = index\"\r\n\t\t\t\t[ngClass]=\"i === realCurrentIndex ? 'active' : ''\"\r\n\t\t\t>\r\n\t\t\t\t<button\r\n\t\t\t\t\tclass=\"pagination\"\r\n\t\t\t\t\tattr.data-aui=\"pagination-dot {{ i + 1 }}\"\r\n\t\t\t\t\t(click)=\"slideTo(i + extraSlidesEachSide, undefined, true)\"\r\n\t\t\t\t\t[attr.aria-label]=\"getPaginationAriaLabel(i)\"\r\n\t\t\t\t>\r\n\t\t\t\t\t<ng-container *ngTemplateOutlet=\"dot\"></ng-container>\r\n\t\t\t\t</button>\r\n\t\t\t</div>\r\n\t\t</div>\r\n\t</div>\r\n</section>\r\n<section *ngIf=\"loadedOnce\" class=\"thumbnails-container\">\r\n\t<ng-content select=\".thumbnails\"></ng-content>\r\n</section>\r\n", styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}.carousel{height:auto;width:100%;overflow:hidden;position:relative}.carousel .carousel-container{width:100%;-webkit-overflow-scrolling:touch}.carousel .carousel-container .slide-wrapper{width:100%;display:flex;flex-direction:row;will-change:transform}.carousel .carousel-container .slide-wrapper.dragging .slide{-webkit-user-select:none;user-select:none}.carousel .carousel-container .slide-wrapper.nostretch{justify-content:center;display:inline-block;text-align:center}.carousel .carousel-container .slide-wrapper.nostretch .slide{width:auto!important;display:inline-block;height:100%}.carousel .carousel-container .slide-wrapper .slide{flex:1 0 auto;height:auto;width:100%;-webkit-user-select:text;user-select:text}.carousel .carousel-container .slide-wrapper.no-transition{transition:none!important}.carousel .carousel-container .slide-wrapper.transition{transition:transform .4s}.carousel .button-wrapper{height:100%;width:100%;position:absolute;top:0;left:0;pointer-events:none}@media print,screen and (max-width: 37.49875em){.carousel .button-wrapper.arrows-bottom .arrow{top:initial;bottom:0}}.carousel .button-wrapper .arrow{cursor:pointer;pointer-events:auto;height:60px;width:60px;position:absolute;top:50%;transform:translateY(-50%);background:transparent;border:none}.carousel .button-wrapper .arrow.prev{left:0}.carousel .button-wrapper .arrow.next{right:0}.carousel .button-wrapper .arrow i{font-size:48px;color:#fff}.carousel .button-wrapper .arrow i.icon-arrow-right{right:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-right{right:16px}}.carousel .button-wrapper .arrow i.icon-arrow-left{left:8px}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i.icon-arrow-left{left:16px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow i{font-size:64px}}@media print,screen and (min-width: 48em){.carousel .button-wrapper .arrow{width:72px;height:72px}}.carousel .button-wrapper .hide-arrows{display:none}.carousel .button-wrapper .pagination-container{position:absolute;display:flex;left:50%;transform:translate(-50%);bottom:0;padding:20px 0 16px;flex-direction:row;align-content:center;justify-content:center;pointer-events:auto}.carousel .button-wrapper .pagination-container .dot-wrapper{padding:0 16px}.carousel .button-wrapper .pagination-container .dot-wrapper .pagination:empty{width:12px;height:12px;border:1px solid #fff;border-radius:50%;cursor:pointer;background-color:#3339;padding:0}.carousel .button-wrapper .pagination-container .dot-wrapper.active .pagination:empty{background-color:#fff;border:1px solid #333}.carousel:hover .hide-arrows{display:block}\n", ".thumbnails .carousel .carousel-container .slide-wrapper .clone *{pointer-events:none}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: CarouselService, decorators: [{
                    type: SkipSelf
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class CarouselModule {
}
CarouselModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CarouselModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselModule, declarations: [CarouselComponent, CarouselThumbnailsComponent], imports: [CommonModule], exports: [CarouselComponent, CarouselThumbnailsComponent] });
CarouselModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CarouselComponent, CarouselThumbnailsComponent],
                    exports: [CarouselComponent, CarouselThumbnailsComponent]
                }]
        }] });

export { CarouselBreakpoint, CarouselComponent, CarouselLabels, CarouselModule, CarouselOptions, CarouselService, CarouselThumbnailsComponent };
